import { error as logErrorToConsole } from '@ms/yammer-console-logging';
import { enqueueTelemetryEvents, getTelemetryClientConfig } from '@ms/yammer-telemetry-store';

import type { AnalyticsEvent, AnalyticsEventName, BaseTelemetryEvent } from '../events';

import { getReportAnalyticsEventsInput } from './getReportAnalyticsEventsInput';
import { getThrottledReportAnalyticsEventsInBatch } from './reportInBatches';
import { reportAnalyticsEventsNow } from './reportTelemetryEventsToServer';

const eventsAllowedForReportNow: AnalyticsEventName[] = [
  'ecs_experiment_treatment',
  'external_link_clicked',
  'feedback_form_opened',
  'feedback_form_submitted',
  'group_over_flow_menu_item_clicked',
  'qanda_app_added',
  'create_teams_meeting_requested',
];

type EnqueueAndThrottleReportAnalyticsEvents = <T extends BaseTelemetryEvent>(events: T[]) => void;
export const enqueueAndThrottleReportAnalyticsEvents: EnqueueAndThrottleReportAnalyticsEvents = <
  T extends BaseTelemetryEvent,
>(
  events: T[]
) => {
  enqueueTelemetryEvents<T>('Analytics', events);
  const throttledReportAction = getThrottledReportAnalyticsEventsInBatch(
    // istanbul ignore next
    getTelemetryClientConfig()?.eventReportIntervalInMilliseconds ?? 0
  );

  throttledReportAction();
};

type ReportAnalyticsEvent = (event: AnalyticsEvent) => void;
export const reportAnalyticsEvent: ReportAnalyticsEvent = (event) => {
  const clientConfig = getTelemetryClientConfig();
  if (!clientConfig) {
    return;
  }

  enqueueAndThrottleReportAnalyticsEvents([event]);
};

type ReportAnalyticsEventNow = (event: AnalyticsEvent) => Promise<void>;

export const reportAnalyticsEventNow: ReportAnalyticsEventNow = async (event) => {
  const clientConfig = getTelemetryClientConfig();
  if (!clientConfig) {
    return;
  }

  if (!eventsAllowedForReportNow.includes(event.name as AnalyticsEventName)) {
    logErrorToConsole('Event not allowed for report now');

    return;
  }

  const analyticsEventInputs = getReportAnalyticsEventsInput([event]);
  await reportAnalyticsEventsNow({
    events: analyticsEventInputs,
  });
};
